/**
 * EL CÓDIGO DE BARRAS COMO ANCLA de la foto.
 *
 * Es la mejor pista de geometría que tiene un documento, y durante mucho tiempo
 * la ignoramos buscando la tarjeta por brillo. Comparado con eso:
 *
 *   - Decodifica o no decodifica. No hay "más o menos": la corrección de errores
 *     Reed-Solomon no devuelve una posición aproximada de algo que no encontró.
 *   - Funciona cuando el OCR no lee NADA. Medido: en una foto de un DNI sobre
 *     una mesa, girada 90°, el OCR devolvió cero rótulos y el código entregó
 *     apellido, nombres, sexo, documento y fechas.
 *   - Es un rectángulo de proporción conocida, así que su caja da el ÁNGULO de
 *     la foto y la ESCALA.
 *   - El catálogo sabe en qué fracción de la tarjeta vive, así que de su caja
 *     sale la tarjeta entera.
 *
 * El heurístico de la mancha clara hacía lo contrario: sobre dos fotos reales
 * devolvió relaciones de lado 0,254 y 0,617 —no es una tarjeta— y sobre una
 * tercera "acertó" con ángulos a 18,7° de 90, que es el cuadrilátero torcido que
 * escorzaba la imagen al enderezarla.
 */
import { ModeloDeDocumento, Cara } from "./catalogo";
import { Punto } from "./geometria";
import { Rectangulo } from "./types";
import { TARJETA_ID1 } from "./tuning";

export interface CodigoUbicado {
  /** Las cuatro esquinas del código en la foto, en orden SI, SD, ID, II. */
  esquinas: [Punto, Punto, Punto, Punto];
}

export interface AnclajePorCodigo {
  /** Grados que hay que girar la foto para poner el documento derecho. */
  anguloGrados: number;
  /** Giro en cuartos de vuelta: lo que hay que aplicar antes de OCR. */
  cuartosDeVuelta: 0 | 90 | 180 | 270;
  /** La tarjeta deducida, alineada a los ejes de la foto ya girada. */
  tarjeta: Rectangulo;
  /** Píxeles por milímetro, de la escala del código. */
  pxPorMm: number;
}

const grados = (rad: number) => (rad * 180) / Math.PI;

/**
 * De la caja del código a la geometría del documento.
 *
 * Necesita saber en qué fracción de la tarjeta vive el código, que es dato del
 * catálogo. Si el modelo no declara esa zona, no hay anclaje: se dice y listo.
 */
export function anclarPorCodigo(
  codigo: CodigoUbicado,
  modelo: ModeloDeDocumento,
  cara: Cara,
  campoDelCodigo = "pdf417"
): AnclajePorCodigo | null {
  const zona = modelo.zonas.find((z) => z.cara === cara && z.campo === campoDelCodigo);
  if (!zona || zona.caja.w <= 0 || zona.caja.h <= 0) return null;

  const [si, sd, , ii] = codigo.esquinas;
  const largo = Math.hypot(sd.x - si.x, sd.y - si.y);
  const altoCodigo = Math.hypot(ii.x - si.x, ii.y - si.y);
  if (largo <= 0 || altoCodigo <= 0) return null;

  const anguloGrados = grados(Math.atan2(sd.y - si.y, sd.x - si.x));
  // El giro se redondea al cuarto de vuelta: es lo que hay que aplicarle a los
  // píxeles antes del OCR. La inclinación fina la corrige el enderezado, no esto.
  const normalizado = ((anguloGrados % 360) + 360) % 360;
  const cuartos = (Math.round(normalizado / 90) * 90) % 360;
  const cuartosDeVuelta = cuartos as 0 | 90 | 180 | 270;

  // La tarjeta, deducida de la fracción que el catálogo declara para el código.
  const anchoTarjeta = largo / zona.caja.w;
  const altoTarjeta = anchoTarjeta / TARJETA_ID1.relacionDeLados;
  const izquierda = si.x - zona.caja.x * anchoTarjeta;
  const arriba = si.y - zona.caja.y * altoTarjeta;

  return {
    anguloGrados,
    cuartosDeVuelta,
    tarjeta: { x: izquierda, y: arriba, w: anchoTarjeta, h: altoTarjeta },
    pxPorMm: anchoTarjeta / TARJETA_ID1.anchoMm,
  };
}

/**
 * Verificación barata de que el anclaje es creíble.
 *
 * Un PDF417 del DNI mide, medido sobre dos documentos reales, entre 4,3 y 4,6
 * veces más largo que alto. Si la caja que devolvió el decodificador no tiene
 * esa forma, algo se leyó mal y no conviene apoyar la geometría ahí.
 */
export function codigoTieneFormaEsperada(codigo: CodigoUbicado, tolerancia = 0.25): boolean {
  const [si, sd, , ii] = codigo.esquinas;
  const largo = Math.hypot(sd.x - si.x, sd.y - si.y);
  const altoCodigo = Math.hypot(ii.x - si.x, ii.y - si.y);
  if (altoCodigo <= 0) return false;
  const relacion = largo / altoCodigo;
  return Math.abs(relacion - 4.45) / 4.45 <= tolerancia;
}

// ---------------------------------------------------------------------------
// La tarjeta entera desde las cuatro esquinas del código (M1)
// ---------------------------------------------------------------------------

type Cuadrilatero = [Punto, Punto, Punto, Punto];

/**
 * Une las detecciones de UN mismo símbolo en un solo cuadrilátero.
 *
 * MEDIDO sobre el corpus: con la foto ampliada, zxing devuelve el mismo PDF417
 * como DOS detecciones —cada una con una parte de las filas— y la primera es
 * una tira de 25 px de alto sobre un código de 150. Con esa tira la forma no
 * daba la de un PDF417 (27 a 1 en vez de 4,45 a 1), no se anclaba la tarjeta, y
 * lo que se tapaba como «código» era una línea negra sobre la parte de arriba
 * del código, que quedaba entero a la vista.
 *
 * Se proyectan todas las esquinas sobre los ejes de la primera detección (a lo
 * largo de las filas y a lo ancho) y se toman los extremos: vale con la foto
 * girada.
 */
export function unirDetecciones(detecciones: Cuadrilatero[]): Cuadrilatero | null {
  if (detecciones.length === 0) return null;
  // También una sola detección se lleva a su rectángulo: MEDIDO sobre el corpus,
  // las esquinas de abajo que da zxing para un PDF417 vienen corridas (proporción
  // de hasta 2 veces la real y 11 % de reproyección), y el rectángulo sobre los
  // ejes de la fila de arriba es el código.
  const [si, sd] = detecciones[0];
  const ux = sd.x - si.x, uy = sd.y - si.y;
  const lu = Math.hypot(ux, uy) || 1;
  const u = { x: ux / lu, y: uy / lu };
  // El eje de las columnas: perpendicular, de la fila de arriba a la de abajo
  // (las esquinas vienen en orden SI, SD, ID, II, también con la foto girada).
  const v = { x: -u.y, y: u.x };
  const puntos = detecciones.flat();
  const pu = puntos.map((p) => p.x * u.x + p.y * u.y);
  const pv = puntos.map((p) => p.x * v.x + p.y * v.y);
  const [u0, u1, v0, v1] = [Math.min(...pu), Math.max(...pu), Math.min(...pv), Math.max(...pv)];
  const punto = (a: number, b: number): Punto => ({ x: a * u.x + b * v.x, y: a * u.y + b * v.y });
  return [punto(u0, v0), punto(u1, v0), punto(u1, v1), punto(u0, v1)];
}


// ---------------------------------------------------------------------------
// El control del ancla (revisión, importante 4)
// ---------------------------------------------------------------------------

/**
 * Similitud (giro, escala pareja y traslado) por cuadrados mínimos que lleva
 * `origen` a `destino`. Con cuatro pares, un error en una esquina se reparte en
 * vez de proyectarse: la homografía exacta desde un recuadro que ocupa el 5 % de
 * la tarjeta convertía 2 px de error en una esquina del código en 91 px en la
 * esquina opuesta de la tarjeta.
 */
export function similitud(origen: Punto[], destino: Punto[]): ((p: Punto) => Punto) | null {
  const n = origen.length;
  if (n < 2 || destino.length !== n) return null;
  const mo = origen.reduce((s, p) => ({ x: s.x + p.x / n, y: s.y + p.y / n }), { x: 0, y: 0 });
  const md = destino.reduce((s, p) => ({ x: s.x + p.x / n, y: s.y + p.y / n }), { x: 0, y: 0 });
  let sxx = 0, a = 0, b = 0;
  for (let i = 0; i < n; i++) {
    const ox = origen[i].x - mo.x, oy = origen[i].y - mo.y;
    const dx = destino[i].x - md.x, dy = destino[i].y - md.y;
    sxx += ox * ox + oy * oy;
    a += ox * dx + oy * dy;
    b += ox * dy - oy * dx;
  }
  if (sxx === 0) return null;
  const c = a / sxx, s = b / sxx;
  return (p) => ({
    x: md.x + c * (p.x - mo.x) - s * (p.y - mo.y),
    y: md.y + s * (p.x - mo.x) + c * (p.y - mo.y),
  });
}

/** Las esquinas de la caja del código en milímetros de la tarjeta (SI, SD, ID, II). */
function cajaEnMm(caja: Rectangulo): Punto[] {
  const W = TARJETA_ID1.anchoMm, H = TARJETA_ID1.altoMm;
  return [
    { x: caja.x * W, y: caja.y * H },
    { x: (caja.x + caja.w) * W, y: caja.y * H },
    { x: (caja.x + caja.w) * W, y: (caja.y + caja.h) * H },
    { x: caja.x * W, y: (caja.y + caja.h) * H },
  ];
}

/** Las medidas que deciden si un código sirve de ancla. */
export interface MedidasDelAncla {
  /** Largo sobre alto del código detectado, relativo al de la plantilla (1 = igual). */
  proporcion: number;
  /** Cuánto cae la tarjeta afuera de la imagen, como fracción de su ancho (0 = adentro). */
  afuera: number;
}

/** Los límites del control. Salen del corpus (ver ocr-informe.md, ronda 1). */
export const LIMITES_DEL_ANCLA = {
  /**
   * MEDIDO sobre los 16 frentes 2012 reales con el código decodificado: entre
   * 0,98 y 1,06 de la proporción de la plantilla, y uno en 1,17 cuyo tapado cae
   * bien. Un código al que le falta el 20 % de las filas da 1,25. Es también la
   * relación de lados ID-1 de la tarjeta que daría la homografía exacta desde ese
   * código: para un código rectangular, las dos se desvían lo mismo. Y, como el
   * código llega rectificado (unirDetecciones), el error de reproyección de sus
   * esquinas con la similitud depende solo de esta proporción: controlarla es
   * controlar la reproyección (los 16 dan entre 0,05 % y 1,7 % del largo).
   *
   * No es simétrico: más corto que la plantilla no es ningún código real del
   * corpus, y un código al que le faltan columnas corre la tarjeta mucho más (con
   * el 10 % de columnas de menos, 107 px en una tarjeta de 1000; con el 5 %, 54 px).
   * Mínimo 0,97: los 16 reales dan 0,98 o más.
   */
  proporcionMinima: 0.97,
  proporcionMaxima: 1.2,
  /** La foto puede cortar la tarjeta, no tanto como para dejarla mayormente afuera. */
  afuera: 0.1,
} as const;

/** Mide el código detectado contra la caja que el catálogo le da en la tarjeta. */
export function medirAncla(
  codigo: Cuadrilatero,
  cajaDelCodigo: Rectangulo,
  imagen: { ancho: number; alto: number }
): MedidasDelAncla | null {
  const mm = cajaEnMm(cajaDelCodigo);
  const T = similitud(mm, codigo);
  if (!T) return null;
  const d = (a: Punto, b: Punto) => Math.hypot(a.x - b.x, a.y - b.y);
  const [si, sd, id, ii] = codigo;
  const largo = (d(si, sd) + d(ii, id)) / 2;
  const alto = (d(si, ii) + d(sd, id)) / 2;
  if (largo <= 0 || alto <= 0) return null;
  const proporcionPlantilla = (cajaDelCodigo.w * TARJETA_ID1.anchoMm) / (cajaDelCodigo.h * TARJETA_ID1.altoMm);
  const tarjeta = [
    { x: 0, y: 0 },
    { x: TARJETA_ID1.anchoMm, y: 0 },
    { x: TARJETA_ID1.anchoMm, y: TARJETA_ID1.altoMm },
    { x: 0, y: TARJETA_ID1.altoMm },
  ].map(T);
  const anchoTarjeta = d(tarjeta[0], tarjeta[1]);
  const afuera = Math.max(
    0,
    ...tarjeta.map((p) => Math.max(-p.x, -p.y, p.x - imagen.ancho, p.y - imagen.alto))
  ) / anchoTarjeta;
  return {
    proporcion: largo / alto / proporcionPlantilla,
    afuera,
  };
}

/** Si las medidas del código lo dejan anclar la tarjeta. */
export function anclaCreible(m: MedidasDelAncla | null): boolean {
  if (!m) return false;
  return (
    m.proporcion >= LIMITES_DEL_ANCLA.proporcionMinima &&
    m.proporcion <= LIMITES_DEL_ANCLA.proporcionMaxima &&
    m.afuera <= LIMITES_DEL_ANCLA.afuera
  );
}

/**
 * La tarjeta por el código, con la similitud (ver arriba): sus cuatro esquinas en
 * la imagen, que salen de llevar la tarjeta en milímetros al código detectado.
 */
export function tarjetaPorSimilitud(codigo: Cuadrilatero, cajaDelCodigo: Rectangulo): Cuadrilatero | null {
  const T = similitud(cajaEnMm(cajaDelCodigo), codigo);
  if (!T) return null;
  return [
    { x: 0, y: 0 },
    { x: TARJETA_ID1.anchoMm, y: 0 },
    { x: TARJETA_ID1.anchoMm, y: TARJETA_ID1.altoMm },
    { x: 0, y: TARJETA_ID1.altoMm },
  ].map(T) as Cuadrilatero;
}
